// First Created - 26 May 2026
// Author - Atul Pandey
// Purpose - To show a collective Session QNA board for users and coaches to respond to session questions

import React, { useEffect, useMemo, useState } from "react"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLight3 from "@/utils/EtContentEditorLight3"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"

import {
  PlusIcon,
  MinusIcon,
  PencilSquareIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline"

import {
  QuestionMarkCircleIcon,
  UserCircleIcon,
  AcademicCapIcon,
  // 31 May 2026
  ChartBarIcon,
  UsersIcon,
} from "@heroicons/react/24/solid"

import {
  useGetUserSessionQNAResponsesQuery,
  useCreateNewUserSessionQNAResponseMutation,
  useUpdateUserSessionQNAResponseMutation,
} from "./userSessionQNAResponsesApiSlice"

// 31 May 2026 - Users + session execution denominator for QNA completion
import { useGetUsersQuery } from "@/features/users/usersApiSlice"
import { useGetUserTrailSessionExecutionsQuery } from "@/features/userTrailSessions/userTrailSessionExecutionsApiSlice"

// 31 May 2026 - Charts for Session QNA response contribution trends
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend, // 26 Jun 2026
  Cell,
} from "recharts"

// 31 May 2026 - Circular contribution progress
import { CircularProgressbarWithChildren } from "react-circular-progressbar"
import "react-circular-progressbar/dist/styles.css"

import User from "@/components/common/user/User"
import { userProfileIcon2 } from "@/assets/img/imgAssets"

// 15 Aug 2026
import {
    useDownloadSessionQNAPowerPointMutation,
    useDownloadSessionQNAPdfMutation,       
    useEmailSessionQNAInlineMutation,
    useEmailSessionQNAPdfMutation,
    useEmailSessionQNAPowerPointMutation,
} from '@/features/reporting/sessionQNAs/sessionQNAReportsApiSlice'

// 16 Aug 2026
import useAuth from '@/hooks/useAuth'

const EtUserSessionQNABoard = ({
  userId,
  userOrgId,
  trailId,
  sessionId,
  cohortId,   // 25 Jun 2026
  cohort,   // 25 Jun 2026
  sessionQNASets = [],
  responseRole = "User",
  boardTitle = "Session Q&A Board",
}) => {
  // First Created - 26 May 2026
  // Author - Atul Pandey
  // Purpose - To show QNA questions attached to a session and allow users / coaches to respond
  // Updated - 31 May 2026 - refined for charts & trends
  // Updated - 25 Jun 2026 - refined for cohortId
  // Updated - 26 Jun 2026 - refined for coach's response
  // Updated - 26 Jun 2026 - refined for eligible users count from OrgTrailSessionCohort rather than UserTrailSessionExecutions (no longer used now. Maintained for reference only)
  // Updated - 09 Aug 2026 - session QNA report
  // Updated - 12 Aug 2026 - JSX refinements for stacking by questions response snapshot below trends
  // Updated - 15 Aug 2026 - refinements to use all the session report gen directly in this comp.
  // Updated - 16 Aug 2026 - useAuth to control download and email options

  // 0 - Validate incoming data
  // console.log('userId', userId)
  // console.log('userOrgId', userOrgId)
  // console.log('trailId', trailId)
  // console.log('sessionId', sessionId)
  // console.log('cohortId', cohortId)
  // console.log('cohort', cohort)
  // console.log('sessionQNASets', sessionQNASets)
  // console.log('responseRole', responseRole)
  // console.log('boardTitle', boardTitle)

  // 0 - 16 Aug 2026 - Session Q&A report access controls
  const {
      isEtCoach,
      isEtAdmin,
      isSponsor,
  } =
      useAuth()


  const canManageSessionQNAReportArtifacts =
      isEtCoach ||
      isEtAdmin


  const canEmailSessionQNAPdf =
      canManageSessionQNAReportArtifacts ||
      isSponsor


  // 1 - Local state
  const [showBoard, setShowBoard] = useState(true)
  const [showQuestionDetails, setShowQuestionDetails] = useState({})
  const [editorValues, setEditorValues] = useState({})
  const [editingResponseIds, setEditingResponseIds] = useState({})
  const [showResponseEditor, setShowResponseEditor] = useState({}) // 26 May 2026

  // 31 May 2026 - QNA contribution analytics tab
  const [activeInsightTab, setActiveInsightTab] = useState("question")

  // 15 Aug 2026 - Main Session Q&A display tab
  const [
    activeSessionQNATab,
    setActiveSessionQNATab,
  ] =
    useState("trends")

  // 2 - Fetch responses for this session
  const {
    data: qnaResponsesData,
    isLoading,
    isError,
    error,
  } = useGetUserSessionQNAResponsesQuery(
    {
      trailId,
      sessionId,
    },
    {
      skip: !trailId || !sessionId || !cohortId, // 25 Jun 2026 - added cohortId
    }
  )

  // 31 May 2026 - Fetch all users to determine org users
  const { data: allUsersData } = useGetUsersQuery()

  // 31 May 2026 - Fetch session executions to determine users in this trail/session
  const { data: userTrailSessionExecutionsData } =
    useGetUserTrailSessionExecutionsQuery()

  // 31 May 2026 - Extract entity adapter data safely
  const extractEntitiesFromAdapter = dataAdapter => {
    if (!dataAdapter) return []
    if (Array.isArray(dataAdapter)) return dataAdapter
    if (!dataAdapter.ids || !dataAdapter.entities) return []
    return dataAdapter.ids.map(id => dataAdapter.entities[id])
  }

  // 31 May 2026 - Normalize Mongo populated / non-populated ids
  const normalizeId = value => {
    if (!value) return ""
    return (value?._id || value)?.toString()
  }

  // 3 - Mutations
  const [
    createNewUserSessionQNAResponse,
    { isLoading: isCreatingResponse },
  ] = useCreateNewUserSessionQNAResponseMutation()

  const [
    updateUserSessionQNAResponse,
    { isLoading: isUpdatingResponse },
  ] = useUpdateUserSessionQNAResponseMutation()

  // 15 Aug 2026
  const [
      downloadSessionQNAPowerPoint,
      {
          isLoading:
              isDownloadingSessionQNAPowerPoint,
      },
  ] =
      useDownloadSessionQNAPowerPointMutation()


  const [
      downloadSessionQNAPdf,
      {
          isLoading:
              isDownloadingSessionQNAPdf,
      },
  ] =
      useDownloadSessionQNAPdfMutation()


  const [
      emailSessionQNAInline,
      {
          isLoading:
              isEmailingSessionQNAInline,
      },
  ] =
      useEmailSessionQNAInlineMutation()


  const [
      emailSessionQNAPowerPoint,
      {
          isLoading:
              isEmailingSessionQNAPowerPoint,
      },
  ] =
      useEmailSessionQNAPowerPointMutation()


  const [
      emailSessionQNAPdf,
      {
          isLoading:
              isEmailingSessionQNAPdf,
      },
  ] =
      useEmailSessionQNAPdfMutation()

  // 15 Aug 2026
  const isProcessingSessionQNAReport =
    isDownloadingSessionQNAPowerPoint ||
    isDownloadingSessionQNAPdf ||
    isEmailingSessionQNAInline ||
    isEmailingSessionQNAPowerPoint ||
    isEmailingSessionQNAPdf

  // 4 - Convert all fetched session responses to an array.
  const allQnaResponses =
    qnaResponsesData?.ids?.map(id => qnaResponsesData?.entities[id]) || []

  // console.log('allQ&AResponses', allQnaResponses)

  // 25 Jun 2026 - Filter session responses locally by the active board cohort.
  // The backend intentionally returns all permitted session responses so future
  // screens can show all cohorts or cohort comparisons.
  const qnaResponses = allQnaResponses.filter(response => {
    return (
      response?.active !== false &&
      normalizeId(response?.cohortId) === normalizeId(cohortId)
    )
  })

  // console.log('cohort Q&AResponses', qnaResponses)

  // 5 - Normalize attached QNA sets from the session
  const activeSessionQNASets = useMemo(() => {
    return (sessionQNASets || [])
      ?.filter(item => item?.active !== false && item?.sessionQNASetId)
      ?.slice()
      ?.sort(
        (a, b) =>
          (a?.sessionQNASetSequence || 0) -
          (b?.sessionQNASetSequence || 0)
      )
  }, [sessionQNASets])

  // 6 - Flatten all active questions from all attached QNA sets
  const boardQuestions = useMemo(() => {
    const flattenedQuestions = []

    activeSessionQNASets.forEach(sessionQNASetItem => {
      const qnaSet = sessionQNASetItem?.sessionQNASetId
      const questions = qnaSet?.sessionQNAQuestions || []

      questions
        ?.filter(question => question?.active !== false)
        ?.slice()
        ?.sort(
          (a, b) =>
            (a?.questionSequence || 0) -
            (b?.questionSequence || 0)
        )
        ?.forEach(question => {
          flattenedQuestions.push({
            sessionQNASetLinkId: sessionQNASetItem?._id,
            sessionQNASetId: qnaSet?._id || qnaSet?.id,
            sessionQNASetName: qnaSet?.sessionQNAName,
            sessionQNASetDesc: qnaSet?.sessionQNADesc,
            sessionQNASetSequence: sessionQNASetItem?.sessionQNASetSequence,
            questionId: question?._id,
            questionText: question?.questionText,
            questionDesc: question?.questionDesc,
            questionPrompt: question?.questionPrompt,
            questionType: question?.questionType,
            questionSequence: question?.questionSequence,
          })
        })
    })

    return flattenedQuestions
  }, [activeSessionQNASets])

  // 26 Jun 2026 - All users are used only to enrich cohort member details
  // for the By User analytics display.
  const allUsers = useMemo(() => {
    return extractEntitiesFromAdapter(allUsersData)
  }, [allUsersData])

  // 26 Jun 2026 - Coach IDs are excluded defensively from eligible users.
  // Normally coaches are maintained in cohort.coachIds, while cohort users
  // are maintained in cohort.cohortUserIds.
  const cohortCoachIdSet = useMemo(() => {
    return new Set(
      (cohort?.coachIds || [])
        .map(item => normalizeId(item?.coachId))
        .filter(Boolean)
    )
  }, [cohort])

  // 26 Jun 2026 - Cohort membership is the source of truth for the
  // Session Q&A eligible-user denominator.
  const cohortEligibleUserIds = useMemo(() => {
    return [
      ...new Set(
        (cohort?.cohortUserIds || [])
          .filter(member => member?.active !== false)
          .map(member => normalizeId(member?.userId))
          .filter(
            memberUserId =>
              memberUserId &&
              !cohortCoachIdSet.has(memberUserId)
          )
      ),
    ]
  }, [cohort, cohortCoachIdSet])

  // 26 Jun 2026 - Build display-ready eligible users from cohort membership.
  // Fall back to the cohort member object if the all-users query has not
  // returned a matching user object yet.
  const eligibleSessionUsers = useMemo(() => {
    return cohortEligibleUserIds.map(eligibleUserId => {
      const matchingUser = allUsers.find(
        user => normalizeId(user?._id) === eligibleUserId
      )

      if (matchingUser) {
        return matchingUser
      }

      const matchingCohortMember = (cohort?.cohortUserIds || []).find(
        member => normalizeId(member?.userId) === eligibleUserId
      )

      const memberUser = matchingCohortMember?.userId

      if (memberUser && typeof memberUser === "object") {
        return memberUser
      }

      return {
        _id: eligibleUserId,
        name: "Participant",
        username: "",
      }
    })
  }, [allUsers, cohort, cohortEligibleUserIds])

  // 26 Jun 2026 - Prior code commented. Kept for reference only
  // 31 May 2026 - All users
  // const allUsers = useMemo(() => {
  //   return extractEntitiesFromAdapter(allUsersData)
  // }, [allUsersData])

  // 31 May 2026 - All UserTrailSessionExecution records
  const userTrailSessionExecutions = useMemo(() => {
    return extractEntitiesFromAdapter(userTrailSessionExecutionsData)
  }, [userTrailSessionExecutionsData])

  // 31 May 2026 - Org users
  const orgUsers = useMemo(() => {
    return allUsers.filter(user => {
        return (
        normalizeId(user?.userOrgId) === normalizeId(userOrgId) &&
        user?.active !== false
        )
    })
  }, [allUsers, userOrgId])

  // 7 - Helper to build stable editor key per question
  const getQuestionKey = question => {
    return `${question?.sessionQNASetId}-${question?.questionId}`
  }

  // 8 - Get responses for a specific question
  const getResponsesForQuestion = question => {
    return qnaResponses?.filter(response =>
      (response?.sessionQNASetId?._id || response?.sessionQNASetId) ===
        question?.sessionQNASetId &&
      response?.sessionQNAQuestionId === question?.questionId &&
      response?.active !== false
    )
  }

//   console.log('Question Responses', qnaResponses)

  // 9 - Get current user's own response for a specific question and role
  const getOwnResponseForQuestion = question => {
    return qnaResponses?.find(response =>
      (response?.userId?._id || response?.userId) === userId &&
      (response?.sessionQNASetId?._id || response?.sessionQNASetId) ===
        question?.sessionQNASetId &&
      response?.sessionQNAQuestionId === question?.questionId &&
      response?.responseRole === responseRole &&
      response?.active !== false
    )
  }


  // 31 May 2026 - Get responder display name
  const getResponderName = response => {
    return (
        response?.userId?.name ||
        response?.userId?.username ||
        "Participant"
    )
  }

  // 31 May 2026 - QNA response contribution analytics
  const qnaContributionInsights = useMemo(() => {
    const activeResponses = (qnaResponses || []).filter(
        response => response?.active !== false
    )

    // 26 Jun 2026 - Only eligible cohort-user responses count toward
    // user completion percentages and expected-response calculations.
    const eligibleUserIdSet = new Set(
      cohortEligibleUserIds.map(id => id.toString())
    )

    const activeUserResponses = activeResponses.filter(response => {
      return (
        response?.responseRole === "User" &&
        eligibleUserIdSet.has(normalizeId(response?.userId))
      )
    })

    const activeCoachResponses = activeResponses.filter(
      response => response?.responseRole === "Coach"
    )

    const totalQuestions = boardQuestions?.length || 0

    // 26 Jun 2026
    // User responses are the primary participation and completion measure.
    const totalResponses = activeUserResponses.length

    // Coach responses are shown separately and do not affect user completion.
    const totalCoachResponses = activeCoachResponses.length

    // const totalResponses = activeResponses?.length || 0

    // 1 - Unique responders across this board
    const responderMap = new Map()

    // activeResponses.forEach(response => {                // 26 Jun 2026 - prior code commented
      activeUserResponses.forEach(response => {             // 26 Jun 2026 - replaced with this line
        const responderId = normalizeId(response?.userId)

        if (!responderId) return

        if (!responderMap.has(responderId)) {
        responderMap.set(responderId, {
            userId: responderId,
            userName: getResponderName(response),
            username: response?.userId?.username,
            responseRole: response?.responseRole,
            answeredQuestionKeys: new Set(),
            totalResponses: 0,
        })
        }

        const responder = responderMap.get(responderId)

        const responseQuestionKey = `${normalizeId(
        response?.sessionQNASetId
        )}-${response?.sessionQNAQuestionId}`

        responder.answeredQuestionKeys.add(responseQuestionKey)
        responder.totalResponses += 1
    })



    // 26 Jun 2026 - True denominator is active non-coach users in the selected cohort.
    const totalEligibleUsers = cohortEligibleUserIds?.length || 0
    
    // 26 Jun 2026 - - Prior code commented. Kept for reference only
    // 31 May 2026 - True denominator is all eligible users in same org + trail + session
    // const totalEligibleUsers = eligibleSessionUsers?.length || 0

    // 2 - Question level contribution
    const questionContributionData = boardQuestions.map((question, index) => {
        const questionKey = getQuestionKey(question)

        const responsesForQuestion = activeResponses.filter(response =>
        normalizeId(response?.sessionQNASetId) ===
            normalizeId(question?.sessionQNASetId) &&
        response?.sessionQNAQuestionId?.toString() ===
            question?.questionId?.toString()
        )

        // 26 May 2026
        const userResponsesForQuestion = responsesForQuestion.filter(response => {
          return (
            response?.responseRole === "User" &&
            eligibleUserIdSet.has(normalizeId(response?.userId))
          )
        })

        const coachResponsesForQuestion = responsesForQuestion.filter(
          response => response?.responseRole === "Coach"
        )

        const uniqueRespondersForQuestion = new Set(
          userResponsesForQuestion
            .map(response => normalizeId(response?.userId))
            .filter(Boolean)
        )

        const userResponses = userResponsesForQuestion.length
        const coachResponses = coachResponsesForQuestion.length

        const coachCompletionPercent = coachResponses > 0 ? 100 : 0

        // 26 Jun 2026 - Prior code commented. Kept for reference only
        // const uniqueRespondersForQuestion = new Set(
        // responsesForQuestion
        //     .map(response => normalizeId(response?.userId))
        //     .filter(Boolean)
        // )

        // const userResponses = responsesForQuestion.filter(
        // response => response?.responseRole === "User"
        // )?.length

        // const coachResponses = responsesForQuestion.filter(
        // response => response?.responseRole === "Coach"
        // )?.length

        // 31 May 2026 - Question response % is based on eligible session users
        const responsePercent =
        totalEligibleUsers > 0
            ? Math.round(
                (uniqueRespondersForQuestion.size / totalEligibleUsers) * 100
            )
            : 0

        return {
        questionKey,
        questionId: question?.questionId,
        questionLabel: `Q${index + 1}`,
        questionText: question?.questionText,
        sessionQNASetId: question?.sessionQNASetId,
        sessionQNASetName: question?.sessionQNASetName || "Q&A Set",

        // 26 Jun 2026
        // responseCount intentionally means User response count everywhere in the UI.
        responseCount: userResponses,

        // Coach responses remain separate and do not affect completion percentages.
        coachResponseCount: coachResponses,

        // responseCount: responsesForQuestion.length,


        uniqueResponderCount: uniqueRespondersForQuestion.size,
        userResponses,
        coachResponses,
        
        responsePercent,
        coachCompletionPercent,

        }
    })

    // 3 - QNA set level completion
    const setContributionMap = new Map()

    boardQuestions.forEach(question => {
        const setId = normalizeId(question?.sessionQNASetId)

        if (!setContributionMap.has(setId)) {
        setContributionMap.set(setId, {
            sessionQNASetId: setId,
            sessionQNASetName: question?.sessionQNASetName || "Q&A Set",
            totalQuestions: 0,
            //26 Jun 2026
            totalUserResponses: 0,
            totalCoachResponses: 0, 
            // totalResponses: 0,
            answeredQuestions: 0,
            uniqueResponders: new Set(),
        })
        }

        const setItem = setContributionMap.get(setId)
        const questionKey = getQuestionKey(question)

        const questionStat = questionContributionData.find(
        item => item.questionKey === questionKey
        )

        setItem.totalQuestions += 1
        // 26 Jun 2026
        setItem.totalUserResponses += questionStat?.userResponses || 0
        setItem.totalCoachResponses += questionStat?.coachResponses || 0
        // setItem.totalResponses += questionStat?.responseCount || 0

        // 26 Jun 2026
        if ((questionStat?.userResponses || 0) > 0) {
          setItem.answeredQuestions += 1
        }
        // if ((questionStat?.responseCount || 0) > 0) {
        // setItem.answeredQuestions += 1
        // }

        // 26 Jun 2026
        activeUserResponses
        .filter(response => normalizeId(response?.sessionQNASetId) === setId)
        .forEach(response => {
          const responderId = normalizeId(response?.userId)

          if (responderId) {
            setItem.uniqueResponders.add(responderId)
          }
        })
        // activeResponses
        // .filter(response => normalizeId(response?.sessionQNASetId) === setId)
        // .forEach(response => {
        //     const responderId = normalizeId(response?.userId)
        //     if (responderId) setItem.uniqueResponders.add(responderId)
        // })
    })

    // 31 May 2026 - QNA set level completion based on eligible users x questions
    const setContributionData = Array.from(setContributionMap.values()).map(
    setItem => {
        const totalExpectedResponses =
        (totalEligibleUsers || 0) * (setItem.totalQuestions || 0)

        // 26 Jun 2026
        const setCompletionPercent =
        totalExpectedResponses > 0
          ? Math.round(
              (setItem.totalUserResponses / totalExpectedResponses) * 100
            )
          : 0

        // const setCompletionPercent =
        // totalExpectedResponses > 0
        //     ? Math.round((setItem.totalResponses / totalExpectedResponses) * 100)
        //     : 0

        return {
        ...setItem,

        // Actual responders in this set
        uniqueResponderCount: setItem.uniqueResponders.size,

        // Existing values
        setCompletionPercent,

        // 31 May 2026 - New values for better display
        totalExpectedResponses,

        // 26 Jun 2026
        completedResponses: setItem.totalUserResponses,
        coachResponses: setItem.totalCoachResponses,
        // completedResponses: setItem.totalResponses,
        }
    }
    )

    // 4 - User response % by QNA set and overall
    // 31 May 2026 - Important: build from eligibleSessionUsers, not responderMap,
    // so users with 0 responses also appear as 0%.
    const userContributionData = eligibleSessionUsers.map(eligibleUser => {
        const eligibleUserId = normalizeId(eligibleUser?._id)

        const responder = responderMap.get(eligibleUserId) || {
        userId: eligibleUserId,
        userName: eligibleUser?.name || eligibleUser?.username || "Participant",
        username: eligibleUser?.username,
        responseRole: "User",
        answeredQuestionKeys: new Set(),
        totalResponses: 0,
        }

        const setCompletion = setContributionData.map(setItem => {
        const setQuestions = boardQuestions.filter(
            question =>
            normalizeId(question?.sessionQNASetId) === setItem.sessionQNASetId
        )

        const answeredInSet = setQuestions.filter(question => {
            const questionKey = getQuestionKey(question)
            return responder.answeredQuestionKeys.has(questionKey)
        })?.length

        const responsePercent =
            setQuestions.length > 0
            ? Math.round((answeredInSet / setQuestions.length) * 100)
            : 0

        return {
            sessionQNASetId: setItem.sessionQNASetId,
            sessionQNASetName: setItem.sessionQNASetName,
            answeredQuestions: answeredInSet,
            totalQuestions: setQuestions.length,
            responsePercent,
        }
        })

        const overallResponsePercent =
        totalQuestions > 0
            ? Math.round(
                (responder.answeredQuestionKeys.size / totalQuestions) * 100
            )
            : 0

        return {
        userId: responder.userId,
        userName: responder.userName,
        username: responder.username,
        responseRole: responder.responseRole,
        totalResponses: responder.totalResponses,
        answeredQuestions: responder.answeredQuestionKeys.size,
        totalQuestions,
        overallResponsePercent,
        setCompletion,
        }
    })

    userContributionData.sort(
        (a, b) =>
        b.overallResponsePercent - a.overallResponsePercent ||
        b.totalResponses - a.totalResponses
    )

    return {
        totalQuestions,
        totalResponses,
        totalCoachResponses, // 26 Jun 2026

        // 31 May 2026 - True denominator
        totalEligibleUsers,

        // 31 May 2026 - Actual responders only, useful for display
        totalResponders: responderMap.size,

        eligibleSessionUsers,
        questionContributionData,
        setContributionData,
        userContributionData,
    }
  }, [
    qnaResponses, 
    boardQuestions, 
    eligibleSessionUsers,
    cohortEligibleUserIds, // 26 Jun 2026
  ])

  // 9a - Check if a response belongs to the current user
  const isOwnResponse = response => {
    return (
        (response?.userId?._id || response?.userId)?.toString() === userId?.toString() &&
        response?.responseRole === responseRole
    )
  }

  // 10 - Seed editor values from existing own responses
  useEffect(() => {
    if (!boardQuestions?.length || !qnaResponses?.length) return

    const seededValues = {}
    const seededEditingIds = {}

    boardQuestions.forEach(question => {
      const questionKey = getQuestionKey(question)
      const ownResponse = getOwnResponseForQuestion(question)

      if (ownResponse) {
        seededValues[questionKey] = ownResponse?.responseText || ""
        seededEditingIds[questionKey] = ownResponse?._id
      }
    })

    setEditorValues(prev => ({
      ...prev,
      ...seededValues,
    }))

    setEditingResponseIds(prev => ({
      ...prev,
      ...seededEditingIds,
    }))
  }, [qnaResponsesData, boardQuestions?.length])

  // 11 - Toggle question details
  const handleToggleQuestionDetails = questionKey => {
    setShowQuestionDetails(prev => ({
      ...prev,
      [questionKey]: !prev[questionKey],
    }))
  }

  // 12 - Editor change
  const handleEditorChange = (questionKey, value) => {
    setEditorValues(prev => ({
      ...prev,
      [questionKey]: value,
    }))
  }

  // 12a - Open response editor only when user clicks edit
  const handleOpenResponseEditor = question => {
    const questionKey = getQuestionKey(question)
    const ownResponse = getOwnResponseForQuestion(question)

    setEditorValues(prev => ({
        ...prev,
        [questionKey]: ownResponse?.responseText || prev[questionKey] || "",
    }))

    setEditingResponseIds(prev => ({
        ...prev,
        [questionKey]: ownResponse?._id || prev[questionKey],
    }))

    setShowResponseEditor(prev => ({
        ...prev,
        [questionKey]: true,
    }))
  }

  // 12b - Close response editor
  const handleCloseResponseEditor = question => {
    const questionKey = getQuestionKey(question)

    setShowResponseEditor(prev => ({
        ...prev,
        [questionKey]: false,
    }))
  }

  // 13 - Save response
  const handleSaveResponse = async question => {
    const questionKey = getQuestionKey(question)
    const responseText = editorValues[questionKey] || ""
    const existingResponseId = editingResponseIds[questionKey]

    if (!responseText?.trim()) {
      return
    }

    const responsePayload = {
      userId,
      userOrgId,
      trailId,
      sessionId,
      cohortId,   // 25 Jun 2026
      sessionQNASetId: question.sessionQNASetId,
      sessionQNAQuestionId: question.questionId,
      responseRole,
      responseText,
      responseVisibility: "Cohort",
    }

    try {
  if (existingResponseId) {
    await updateUserSessionQNAResponse({
      id: existingResponseId,
      ...responsePayload,
    }).unwrap()
  } else {
    const createdResponse = await createNewUserSessionQNAResponse(responsePayload).unwrap()
  }

  setShowResponseEditor(prev => ({
    ...prev,
    [questionKey]: false,
  }))
    } catch (error) {
    console.error(error)
    }
  }

  // 14 - Render helpers
  const createMarkup = htmlContent => {
    return { __html: htmlContent }
  }

  // 15 Aug 2026 - Return a browser safe filename segment.
  const getSafeFileNamePart = (
    value,
    fallback = "Report"
  ) => {
    const normalizedValue =
      String(
        value ||
        fallback
      )
        .trim()
        .replace(
          /[^a-z0-9]+/gi,
          "_"
        )
        .replace(
          /^_+|_+$/g,
          ""
        )

    return (
      normalizedValue ||
      fallback
    )
  }

  // 15 Aug 2026 - Download Session Q&A PowerPoint report.
  const handleDownloadSessionQNAPowerPoint =
    async () => {
      try {
        const powerPointBlob =
          await downloadSessionQNAPowerPoint({
            orgId:
              userOrgId,

            trailId,

            sessionId,

            cohortId,
          })
            .unwrap()


        if (
          !powerPointBlob ||
          !(
            powerPointBlob instanceof
            Blob
          )
        ) {
          throw new Error(
            "The Session Q&A PowerPoint response was not a valid file."
          )
        }


        const downloadUrl =
          window
            .URL
            .createObjectURL(
              powerPointBlob
            )


        const fileName =
          `${getSafeFileNamePart(
            boardTitle,
            "Session_QNA"
          )}_Report.pptx`


        const downloadLink =
          document
            .createElement(
              "a"
            )

        downloadLink.href =
          downloadUrl

        downloadLink.download =
          fileName

        document
          .body
          .appendChild(
            downloadLink
          )

        downloadLink
          .click()

        document
          .body
          .removeChild(
            downloadLink
          )

        window
          .URL
          .revokeObjectURL(
            downloadUrl
          )

      } catch (
        error
      ) {
        console.error(
          "Session Q&A PowerPoint download failed:",
          error
        )

        window.alert(
          "Session Q&A PowerPoint could not be generated."
        )
      }
  }

  // 15 Aug 2026 - Download Session Q&A PDF report.
  const handleDownloadSessionQNAPdf =
    async () => {
      try {
        const pdfBlob =
          await downloadSessionQNAPdf({
            orgId:
              userOrgId,

            trailId,

            sessionId,

            cohortId,
          })
            .unwrap()


        if (
          !pdfBlob ||
          !(
            pdfBlob instanceof
            Blob
          )
        ) {
          throw new Error(
            "The Session Q&A PDF response was not a valid file."
          )
        }


        const downloadUrl =
          window
            .URL
            .createObjectURL(
              pdfBlob
            )


        const fileName =
          `${getSafeFileNamePart(
            boardTitle,
            "Session_QNA"
          )}_Report.pdf`


        const downloadLink =
          document
            .createElement(
              "a"
            )

        downloadLink.href =
          downloadUrl

        downloadLink.download =
          fileName

        document
          .body
          .appendChild(
            downloadLink
          )

        downloadLink
          .click()

        document
          .body
          .removeChild(
            downloadLink
          )

        window
          .URL
          .revokeObjectURL(
            downloadUrl
          )

      } catch (
        error
      ) {
        console.error(
          "Session Q&A PDF download failed:",
          error
        )

        window.alert(
          "Session Q&A PDF could not be generated."
        )
      }
  }

 // 15 Aug 2026 - Email Session Q&A report as inline HTML.
  const handleEmailSessionQNAInline =
    async () => {
      try {
        const result =
          await emailSessionQNAInline({
            orgId:
              userOrgId,

            trailId,

            sessionId,

            cohortId,
          })
            .unwrap()


        window.alert(
          result
            ?.message ||
          "Session Q&A report emailed successfully."
        )

      } catch (
        error
      ) {
        console.error(
          "Session Q&A inline email failed:",
          error
        )

        window.alert(
          error
            ?.data
            ?.message ||
          "Session Q&A inline report could not be emailed."
        )
      }
  }

  // 15 Aug 2026 - Email Session Q&A PowerPoint report.
  const handleEmailSessionQNAPowerPoint =
    async () => {
      try {
        const result =
          await emailSessionQNAPowerPoint({
            orgId:
              userOrgId,

            trailId,

            sessionId,

            cohortId,
          })
            .unwrap()


        window.alert(
          result
            ?.message ||
          "Session Q&A PowerPoint emailed successfully."
        )

      } catch (
        error
      ) {
        console.error(
          "Session Q&A PowerPoint email failed:",
          error
        )

        window.alert(
          error
            ?.data
            ?.message ||
          "Session Q&A PowerPoint could not be emailed."
        )
      }
  }

  // 15 Aug 2026 - Email Session Q&A PDF report.
  const handleEmailSessionQNAPdf =
    async () => {
      try {
        const result =
          await emailSessionQNAPdf({
            orgId:
              userOrgId,

            trailId,

            sessionId,

            cohortId,
          })
            .unwrap()


        window.alert(
          result
            ?.message ||
          "Session Q&A PDF emailed successfully."
        )

      } catch (
        error
      ) {
        console.error(
          "Session Q&A PDF email failed:",
          error
        )

        window.alert(
          error
            ?.data
            ?.message ||
          "Session Q&A PDF could not be emailed."
        )
      }
  }

  // 31 May 2026 - Render Session QNA contribution insights
  const renderQNAContributionInsights = () => {
    const {
        totalQuestions,
        totalResponses,
        totalCoachResponses, // 26 Jun 2026
        totalEligibleUsers,
        totalResponders,     // 26 Jun 2026
        questionContributionData,
        setContributionData,
        userContributionData,
    } = qnaContributionInsights

    if (!totalQuestions) return null

    // 15 Aug 2026
    const totalQNASets =
    activeSessionQNASets?.length ||
    0

    const expectedUserResponses =
    totalEligibleUsers *
    totalQuestions

    const overallCompletionPercent =
        expectedUserResponses > 0
            ? Math.round(
                (
                    totalResponses /
                    expectedUserResponses
                ) *
                100
            )
            : 0

    return (
        // <div className="m-1 p-2 bg-gray-50 rounded border border-gray-200">

          <div className="m-1 p-3 bg-white rounded border border-gray-200">


          {/*
          |--------------------------------------------------------------------------
          | Session Q&A Response Trends Header
          |--------------------------------------------------------------------------
          */}

          <div className="flex justify-between items-center flex-wrap gap-3">


            {/* Section Identity */}
            <div className="flex justify-start items-center">

              <div className="flex justify-center items-center h-8 w-8 rounded-full bg-orange-100">

                <ChartBarIcon className="h-5 w-5 text-orange-600" />

              </div>


              <div className="mx-2">

                <p className="text-sm font-bold text-gray-700">
                  Session Q&A Response Trends
                </p>

                <p className="text-[10px] text-gray-500">
                  Review response contribution by question, Q&A set and participant.
                </p>

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Insight Tabs
            |--------------------------------------------------------------------------
            */}

            <div className="flex justify-end items-center flex-wrap">

              {[
                {
                  label:
                    "By Question",

                  value:
                    "question",
                },
                {
                  label:
                    "By Q&A Set",

                  value:
                    "set",
                },
                {
                  label:
                    "By User",

                  value:
                    "user",
                },
              ].map(
                tab => (

                  <button
                    key={
                      tab.value
                    }
                    type="button"
                    onClick={() =>
                      setActiveInsightTab(
                        tab.value
                      )
                    }
                    className={`px-3 py-1 mx-1 rounded-full text-[10px] font-bold border ${
                      activeInsightTab ===
                      tab.value
                        ? "bg-orange-600 text-white border-orange-600"
                        : "bg-white text-gray-600 border-gray-200 hover:bg-orange-50"
                    }`}
                  >

                    {
                      tab.label
                    }

                  </button>

                )
              )}

            </div>

          </div>


          {/*
          |--------------------------------------------------------------------------
          | Response Overview Metrics
          |--------------------------------------------------------------------------
          */}

          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2 mt-4">

            {[
              {
                  label:
                      "Q&A Sets",

                  value:
                      totalQNASets,
              },
              {
                label:
                  "Questions",

                value:
                  totalQuestions,
              },
              {
                label:
                  "Eligible Users",

                value:
                  totalEligibleUsers,
              },
              {
                label:
                  "Responders",

                value:
                  totalResponders,
              },
              {
                label:
                  "User Responses",

                value:
                  totalResponses,
              },
              {
                label:
                  "Coach Responses",

                value:
                  totalCoachResponses,
              },
              {
                  label:
                      "Completion",

                  value:
                      `${overallCompletionPercent}%`,
              },
            ].map(
              (
                metric,
                index
              ) => (

                <div
                  key={
                    `${metric.label}-${index}`
                  }
                  className="p-3 bg-gray-50 rounded border border-gray-200 text-center"
                >

                  <p className="text-[9px] text-gray-500">
                    {
                      metric.label
                    }
                  </p>

                  <p className="text-sm font-bold text-orange-600 mt-1">
                    {
                      metric.value
                    }
                  </p>

                </div>

              )
            )}

          </div>

        {/* 1 - By Question - 15 Aug 2026*/}
        {activeInsightTab === "question" && (

          <div className="mt-4 space-y-4">


            {/*
            |--------------------------------------------------------------------------
            | Question Response Histogram
            |--------------------------------------------------------------------------
            */}

            <div className="p-3 bg-gray-50 rounded border border-gray-200">

              <p className="text-[10px] font-bold text-gray-700 mb-1">
                User and Coach Responses by Question
              </p>

              <p className="text-[9px] text-gray-500 mb-3">
                Compare cohort response volume with coach contribution across the Session Q&A questions.
              </p>


              <div className="h-64">

                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >

                  <BarChart
                    data={
                      questionContributionData
                    }
                    margin={{
                      top: 10,
                      right: 10,
                      bottom: 10,
                      left: 0,
                    }}
                  >

                    <CartesianGrid
                      strokeDasharray="3 3"
                    />

                    <XAxis
                      dataKey="questionLabel"
                      tick={{
                        fontSize: 10,
                      }}
                    />

                    <YAxis
                      allowDecimals={
                        false
                      }
                      tick={{
                        fontSize: 10,
                      }}
                    />


                    <Tooltip
                      content={({
                        active,
                        payload,
                      }) => {

                        if (
                          !active ||
                          !payload?.length
                        ) {
                          return null
                        }

                        const item =
                          payload[0]
                            ?.payload

                        return (

                          <div className="bg-white p-2 border border-gray-200 rounded shadow text-[10px] max-w-xs">

                            <p className="font-bold text-gray-700">
                              {
                                item
                                  ?.questionLabel
                              }
                            </p>

                            <p className="text-gray-600 mt-1">
                              {
                                item
                                  ?.questionText
                              }
                            </p>

                            <p className="mt-2">
                              User responses: {
                                item
                                  ?.responseCount ||
                                0
                              }
                            </p>

                            <p>
                              Coach responses: {
                                item
                                  ?.coachResponseCount ||
                                0
                              }
                            </p>

                            <p>
                              User responders: {
                                item
                                  ?.uniqueResponderCount ||
                                0
                              } / {
                                qnaContributionInsights
                                  ?.totalEligibleUsers ||
                                0
                              }
                            </p>

                            <p className="font-bold text-orange-600">
                              User completion: {
                                item
                                  ?.responsePercent ||
                                0
                              }%
                            </p>

                          </div>

                        )
                      }}
                    />


                    <Legend
                      wrapperStyle={{
                        fontSize: "10px",
                        paddingTop: "4px",
                      }}
                    />


                    <Bar
                      dataKey="responseCount"
                      name="User Responses"
                      fill="#65a30d"
                      barSize={20}
                    />


                    <Bar
                      dataKey="coachResponseCount"
                      name="Coach Responses"
                      fill="#ea580c"
                      barSize={20}
                    />

                  </BarChart>

                </ResponsiveContainer>

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Question Response Snapshot
            |--------------------------------------------------------------------------
            */}

            <div className="p-3 bg-gray-50 rounded border border-gray-200">


              {/* Snapshot Header */}
              <div className="flex justify-between items-center mb-3">

                <div>

                  <p className="text-[10px] font-bold text-gray-700">
                    Question Response Snapshot
                  </p>

                  <p className="text-[9px] text-gray-500 mt-1">
                    Completion and contribution by individual Session Q&A question.
                  </p>

                </div>


                <p className="text-[9px] text-gray-500">

                  {
                    questionContributionData
                      .length
                  } question

                  {
                    questionContributionData
                      .length ===
                    1
                      ? ""
                      : "s"
                  }

                </p>

              </div>


              {/*
              |--------------------------------------------------------------------------
              | Scrollable Question Cards
              |--------------------------------------------------------------------------
              */}

              <div className="max-h-[640px] overflow-y-auto pr-2">

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">

                  {questionContributionData.map(
                    item => (

                      <div
                        key={
                          item.questionKey
                        }
                        className="p-3 rounded border border-gray-200 bg-white min-h-[315px] flex flex-col justify-between"
                      >


                        {/*
                        |--------------------------------------------------------------------------
                        | Circular Question Completion
                        |--------------------------------------------------------------------------
                        */}

                        <div className="h-36 w-36 mx-auto">

                          <CircularProgressbarWithChildren
                            value={
                              item
                                ?.responsePercent ||
                              0
                            }
                            circleRatio={
                              0.75
                            }
                            styles={{
                              path: {
                                stroke:
                                  "#f97316",

                                transform:
                                  "rotate(-135deg)",

                                transformOrigin:
                                  "center center",
                              },

                              trail: {
                                stroke:
                                  "#e5e7eb",

                                transform:
                                  "rotate(-135deg)",

                                transformOrigin:
                                  "center center",
                              },
                            }}
                          >

                            <p className="text-xs font-bold text-gray-700">
                              {
                                item
                                  ?.questionLabel
                              }
                            </p>

                            <p className="text-sm font-bold text-orange-600 mt-1">
                              {
                                item
                                  ?.responsePercent ||
                                0
                              }%
                            </p>

                            <p className="text-[9px] text-gray-500">
                              {
                                item
                                  ?.uniqueResponderCount ||
                                0
                              } / {
                                qnaContributionInsights
                                  ?.totalEligibleUsers ||
                                0
                              }
                            </p>

                          </CircularProgressbarWithChildren>

                        </div>


                        {/*
                        |--------------------------------------------------------------------------
                        | Question Identity
                        |--------------------------------------------------------------------------
                        */}

                        <div className="mt-2 px-1 text-center">

                          <p className="text-[10px] font-bold text-gray-700 line-clamp-3">
                            {
                              item
                                ?.questionText
                            }
                          </p>

                          <p className="text-[8px] text-gray-500 mt-1">
                            {
                              item
                                ?.sessionQNASetName ||
                              "Q&A Set"
                            }
                          </p>

                        </div>


                        {/*
                        |--------------------------------------------------------------------------
                        | Question Counts
                        |--------------------------------------------------------------------------
                        */}

                        <div className="grid grid-cols-2 gap-2 mt-3">

                          <div className="p-2 bg-gray-50 rounded border text-center">

                            <p className="text-[8px] text-gray-500">
                              User Responses
                            </p>

                            <p className="text-xs font-bold text-gray-700">
                              {
                                item
                                  ?.responseCount ||
                                0
                              }
                            </p>

                          </div>


                          <div className="p-2 bg-orange-50 rounded border border-orange-100 text-center">

                            <p className="text-[8px] text-gray-500">
                              Coach Responses
                            </p>

                            <p className="text-xs font-bold text-orange-600">
                              {
                                item
                                  ?.coachResponseCount ||
                                0
                              }
                            </p>

                          </div>

                        </div>


                        {/* User Responder Count */}
                        <div className="mt-2 p-2 bg-gray-50 rounded border text-center">

                          <p className="text-[8px] text-gray-500">
                            User Responders
                          </p>

                          <p className="text-[10px] font-bold text-gray-700">
                            {
                              item
                                ?.uniqueResponderCount ||
                              0
                            } / {
                              qnaContributionInsights
                                ?.totalEligibleUsers ||
                              0
                            }
                          </p>

                        </div>

                      </div>

                    )
                  )}

                </div>

              </div>

            </div>

          </div>

        )}

        {/* 2 - By Q&A Set - 15 Aug 2026*/}
        {activeInsightTab === "set" && (

          <div className="mt-4">


            {/*
            |--------------------------------------------------------------------------
            | Q&A Set Completion Snapshot
            |--------------------------------------------------------------------------
            */}

            <div className="p-3 bg-gray-50 rounded border border-gray-200">


              {/* Section Header */}
              <div className="flex justify-between items-center mb-3">

                <div>

                  <p className="text-[10px] font-bold text-gray-700">
                    Q&A Set Completion Snapshot
                  </p>

                  <p className="text-[9px] text-gray-500 mt-1">
                    Review completion, response volume and participation across the Session Q&A sets.
                  </p>

                </div>


                <p className="text-[9px] text-gray-500">

                  {
                    setContributionData
                      .length
                  } Q&A set

                  {
                    setContributionData
                      .length ===
                    1
                      ? ""
                      : "s"
                  }

                </p>

              </div>


              {/*
              |--------------------------------------------------------------------------
              | Scrollable Q&A Set Cards
              |--------------------------------------------------------------------------
              */}

              <div className="max-h-[680px] overflow-y-auto pr-2">

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">

                  {setContributionData.map(
                    setItem => (

                      <div
                        key={
                          setItem
                            .sessionQNASetId
                        }
                        className="p-3 rounded border border-gray-200 bg-white min-h-[325px] flex flex-col justify-between"
                      >


                        {/*
                        |--------------------------------------------------------------------------
                        | Circular Q&A Set Completion
                        |--------------------------------------------------------------------------
                        */}

                        <div className="h-40 w-40 mx-auto">

                          <CircularProgressbarWithChildren
                            value={
                              setItem
                                ?.setCompletionPercent ||
                              0
                            }
                            circleRatio={
                              0.75
                            }
                            styles={{
                              path: {
                                stroke:
                                  "#f97316",

                                transform:
                                  "rotate(-135deg)",

                                transformOrigin:
                                  "center center",
                              },

                              trail: {
                                stroke:
                                  "#e5e7eb",

                                transform:
                                  "rotate(-135deg)",

                                transformOrigin:
                                  "center center",
                              },
                            }}
                          >

                            <p className="text-sm font-bold text-orange-600">
                              {
                                setItem
                                  ?.setCompletionPercent ||
                                0
                              }%
                            </p>

                            <p className="text-[9px] text-gray-500 mt-1">
                              {
                                setItem
                                  ?.completedResponses ||
                                0
                              } / {
                                setItem
                                  ?.totalExpectedResponses ||
                                0
                              }
                            </p>

                          </CircularProgressbarWithChildren>

                        </div>


                        {/*
                        |--------------------------------------------------------------------------
                        | Q&A Set Identity
                        |--------------------------------------------------------------------------
                        */}

                        <div className="mt-2 px-1 text-center">

                          <p className="text-xs font-bold text-gray-700 line-clamp-2">
                            {
                              setItem
                                ?.sessionQNASetName ||
                              "Q&A Set"
                            }
                          </p>

                          <p className="text-[9px] text-gray-500 mt-1">

                            {
                              setItem
                                ?.totalQuestions ||
                              0
                            } question

                            {
                              setItem
                                ?.totalQuestions ===
                              1
                                ? ""
                                : "s"
                            }

                            {" · "}

                            {
                              qnaContributionInsights
                                ?.totalEligibleUsers ||
                              0
                            } eligible user

                            {
                              (
                                qnaContributionInsights
                                  ?.totalEligibleUsers ||
                                0
                              ) ===
                              1
                                ? ""
                                : "s"
                            }

                          </p>

                        </div>


                        {/*
                        |--------------------------------------------------------------------------
                        | Q&A Set Metrics
                        |--------------------------------------------------------------------------
                        */}

                        <div className="grid grid-cols-2 gap-2 mt-3">


                          {/* Responses */}
                          <div className="p-2 bg-gray-50 rounded border text-center">

                            <p className="text-[8px] text-gray-500">
                              Responses
                            </p>

                            <p className="text-xs font-bold text-gray-700">
                              {
                                setItem
                                  ?.completedResponses ||
                                0
                              }
                            </p>

                          </div>


                          {/* Expected */}
                          <div className="p-2 bg-gray-50 rounded border text-center">

                            <p className="text-[8px] text-gray-500">
                              Expected
                            </p>

                            <p className="text-xs font-bold text-gray-700">
                              {
                                setItem
                                  ?.totalExpectedResponses ||
                                0
                              }
                            </p>

                          </div>


                          {/* Responders */}
                          <div className="p-2 bg-gray-50 rounded border text-center">

                            <p className="text-[8px] text-gray-500">
                              Responders
                            </p>

                            <p className="text-xs font-bold text-gray-700">
                              {
                                setItem
                                  ?.uniqueResponderCount ||
                                0
                              }
                            </p>

                          </div>


                          {/* Coach Responses */}
                          <div className="p-2 bg-orange-50 rounded border border-orange-100 text-center">

                            <p className="text-[8px] text-gray-500">
                              Coach Responses
                            </p>

                            <p className="text-xs font-bold text-orange-600">
                              {
                                setItem
                                  ?.coachResponses ||
                                0
                              }
                            </p>

                          </div>

                        </div>


                        {/*
                        |--------------------------------------------------------------------------
                        | Completion Context
                        |--------------------------------------------------------------------------
                        */}

                        <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-100">

                          <p className="text-[9px] text-gray-600 text-center">

                            {
                              setItem
                                ?.completedResponses ||
                              0
                            } of {
                              setItem
                                ?.totalExpectedResponses ||
                              0
                            } expected user responses completed.

                          </p>

                        </div>

                      </div>

                    )
                  )}

                </div>

              </div>

            </div>

          </div>

        )}

        {/* 3 - By User */}
        {activeInsightTab === "user" && (

          <div className="mt-4">


            {/*
            |--------------------------------------------------------------------------
            | User Response Completion
            |--------------------------------------------------------------------------
            */}

            <div className="p-3 bg-gray-50 rounded border border-gray-200">


              {/* Section Header */}
              <div className="flex justify-between items-center mb-3">

                <div className="flex justify-start items-center">

                  <UsersIcon className="h-4 w-4 text-orange-600" />

                  <div className="mx-2">

                    <p className="text-[10px] font-bold text-gray-700">
                      User Response Completion
                    </p>

                    <p className="text-[9px] text-gray-500 mt-1">
                      Review overall and Q&A set completion for eligible cohort participants.
                    </p>

                  </div>

                </div>


                <p className="text-[9px] text-gray-500">

                  {
                    qnaContributionInsights
                      ?.totalEligibleUsers ||
                    0
                  } eligible cohort user

                  {
                    (
                      qnaContributionInsights
                        ?.totalEligibleUsers ||
                      0
                    ) ===
                    1
                      ? ""
                      : "s"
                  }

                </p>

              </div>


              {/*
              |--------------------------------------------------------------------------
              | Empty State
              |--------------------------------------------------------------------------
              */}

              {userContributionData?.length === 0 && (

                <div className="p-3 bg-white rounded border border-gray-200">

                  <p className="text-[10px] text-gray-500">
                    No eligible session users found yet.
                  </p>

                </div>

              )}


              {/*
              |--------------------------------------------------------------------------
              | Scrollable Participant Cards
              |--------------------------------------------------------------------------
              */}

              {userContributionData?.length > 0 && (

                <div className="max-h-[720px] overflow-y-auto pr-2">

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">

                    {userContributionData.map(
                      (
                        userItem,
                        index
                      ) => {

                        const isMe =
                          userItem
                            .userId
                            ?.toString() ===
                          userId
                            ?.toString()


                        return (

                          <div
                            key={
                              `${userItem.userId}-${index}`
                            }
                            className={`p-3 rounded border text-center min-h-[355px] flex flex-col justify-between ${
                              isMe
                                ? "border-lime-300 bg-lime-50"
                                : "border-gray-200 bg-white"
                            }`}
                          >


                            {/*
                            |--------------------------------------------------------------------------
                            | Circular Participant Completion
                            |--------------------------------------------------------------------------
                            */}

                            <div className="h-40 w-40 mx-auto">

                              <CircularProgressbarWithChildren
                                value={
                                  userItem
                                    ?.overallResponsePercent ||
                                  0
                                }
                                circleRatio={
                                  0.75
                                }
                                styles={{
                                  path: {
                                    stroke:
                                      "#f97316",

                                    transform:
                                      "rotate(-135deg)",

                                    transformOrigin:
                                      "center center",
                                  },

                                  trail: {
                                    stroke:
                                      "#e5e7eb",

                                    transform:
                                      "rotate(-135deg)",

                                    transformOrigin:
                                      "center center",
                                  },
                                }}
                              >


                                {/* User Picture */}
                                <div className="flex justify-center">

                                  <User
                                    username={
                                      userItem
                                        ?.username
                                    }
                                    defaultUserPic={
                                      userProfileIcon2
                                    }
                                  />

                                </div>


                                {/* Completion */}
                                <p className="text-xs font-bold text-gray-700 mt-2">

                                  {
                                    userItem
                                      ?.overallResponsePercent ||
                                    0
                                  }%

                                </p>

                              </CircularProgressbarWithChildren>

                            </div>


                            {/*
                            |--------------------------------------------------------------------------
                            | Participant Identity
                            |--------------------------------------------------------------------------
                            */}

                            <div className="mt-2 px-1">

                              <p className="text-xs font-bold text-gray-700 truncate">

                                {
                                  userItem
                                    ?.userName
                                    ?.split(" ")[0] ||
                                  "User"
                                }

                                {isMe && (

                                  <span className="ml-1 text-[9px] text-orange-600 font-bold">
                                    (Me)
                                  </span>

                                )}

                              </p>


                              <p className="text-[9px] text-gray-500 mt-1">

                                {
                                  userItem
                                    ?.answeredQuestions ||
                                  0
                                } / {
                                  userItem
                                    ?.totalQuestions ||
                                  0
                                } questions answered

                              </p>

                            </div>


                            {/*
                            |--------------------------------------------------------------------------
                            | Q&A Set Completion Breakdown
                            |--------------------------------------------------------------------------
                            */}

                            <div className="mt-3">


                              {/* Breakdown Label */}
                              <div className="flex justify-between items-center mb-2 px-1">

                                <p className="text-[8px] font-bold uppercase tracking-wide text-gray-500">
                                  Q&A Set Completion
                                </p>

                                <p className="text-[8px] text-gray-400">

                                  {
                                    userItem
                                      ?.setCompletion
                                      ?.length ||
                                    0
                                  } set

                                  {
                                    (
                                      userItem
                                        ?.setCompletion
                                        ?.length ||
                                      0
                                    ) ===
                                    1
                                      ? ""
                                      : "s"
                                  }

                                </p>

                              </div>


                              {/* Set Completion Cards */}
                              <div className="space-y-2 max-h-32 overflow-y-auto pr-1">

                                {userItem
                                  ?.setCompletion
                                  ?.map(
                                    setItem => (

                                      <div
                                        key={
                                          `${userItem.userId}-${setItem.sessionQNASetId}`
                                        }
                                        className="p-2 bg-gray-50 rounded border border-gray-200"
                                      >


                                        <div className="flex justify-between items-center gap-2">


                                          {/* Set Name */}
                                          <p className="text-[9px] text-gray-600 truncate flex-1 text-left">

                                            {
                                              setItem
                                                ?.sessionQNASetName ||
                                              "Q&A Set"
                                            }

                                          </p>


                                          {/* Completion % */}
                                          <p className="text-[9px] font-bold text-orange-600 shrink-0">

                                            {
                                              setItem
                                                ?.responsePercent ||
                                              0
                                            }%

                                          </p>

                                        </div>


                                        {/* Absolute Completion */}
                                        <div className="flex justify-between items-center mt-1">

                                          <p className="text-[8px] text-gray-400">

                                            {
                                              setItem
                                                ?.answeredQuestions ||
                                              0
                                            } / {
                                              setItem
                                                ?.totalQuestions ||
                                              0
                                            } questions

                                          </p>


                                          <p className="text-[8px] text-gray-400">

                                            {
                                              setItem
                                                ?.responsePercent ||
                                              0
                                            }% complete

                                          </p>

                                        </div>

                                      </div>

                                    )
                                  )}

                              </div>

                            </div>

                          </div>

                        )
                      }
                    )}

                  </div>

                </div>

              )}

            </div>

          </div>

        )}

        </div>
    )
  }

  const renderResponseCard = (response, question) => {
    const responderName =
        response?.userId?.name ||
        response?.userId?.username ||
        "Participant"

    const isCoach = response?.responseRole === "Coach"
    const ownResponse = isOwnResponse(response)

    const responseLabel = ownResponse
        ? "Me"
        : isCoach
        ? "Coach"
        : "My Cohort"

    return (
        <div
        key={response?._id}
        className={`m-1 p-2 rounded border ${
            ownResponse
            ? "bg-lime-50 border-lime-300"
            : isCoach
                ? "bg-orange-50 border-orange-200"
                : "bg-gray-50 border-gray-200"
        }`}
        >
        <div className="flex justify-between items-start">
            <div className="flex justify-start items-center">
            <div className="flex justify-center items-center h-7 w-7 rounded-full bg-white border border-gray-200">
                {isCoach ? (
                <AcademicCapIcon className="h-5 w-5 text-orange-600" />
                ) : (
                <UserCircleIcon className="h-5 w-5 text-gray-500" />
                )}
            </div>

            <div className="mx-2">
                <div className="flex justify-start items-center flex-wrap">
                <p className="text-[10px] font-bold text-gray-700">
                    {responderName}
                </p>

                {ownResponse && (
                    <p className="text-[8px] bg-lime-200 text-lime-800 rounded-full mx-2 px-2 py-[1px]">
                    My Response
                    </p>
                )}
                </div>

                <p className="text-[8px] text-gray-500">
                {responseLabel}
                </p>
            </div>
            </div>

            {ownResponse && (
            <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                <PencilSquareIcon
                className="h-4 w-4 text-orange-600 cursor-pointer"
                onClick={() => handleOpenResponseEditor(question)}
                />
            </div>
            )}
        </div>

        {response?.responseText && (
            <div className="mt-2 text-[10px] text-gray-700 fix-nested-lists">
            <div
                dangerouslySetInnerHTML={createMarkup(response.responseText)}
            ></div>
            </div>
        )}
        </div>
    )
  }


  if (!sessionQNASets || sessionQNASets.length === 0) {
    return null
  }

  if (isLoading) return <PulseLoader color={"#ea580c"} />

  if (isError) {
    return (
      <div className="m-1 p-2 bg-orange-50 rounded">
        <p className="text-xs text-orange-700">
          Error occurred: {error?.data?.message}
        </p>
      </div>
    )
  }

  return (
    <div className="m-1 p-1 bg-gray-200 rounded font-poppins">

      {/* 1 - Board Header */}
      <div className="m-1 p-1 bg-white rounded">
        <div className="flex justify-between items-center">
          <div className="flex justify-start items-center">
            <div
              onClick={() => setShowBoard(prev => !prev)}
              className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-gray-100"
            >
              {showBoard ? (
                <MinusIcon className="h-5 w-5 text-orange-600" />
              ) : (
                <PlusIcon className="h-5 w-5 text-orange-600" />
              )}
            </div>

            <div className="flex justify-center items-center h-8 w-8 rounded-full bg-orange-100">
              <QuestionMarkCircleIcon className="h-5 w-5 text-orange-600" />
            </div>

            <div className="m-1 p-1">
              <p className="text-sm font-bold text-gray-700">
                {boardTitle}
              </p>
              <p className="text-[10px] text-gray-500">
                Respond to the session questions and see the cohort reflections.
              </p>
            </div>
          </div>

          <div className="flex justify-end items-center mx-2">

            {/* 15 Aug 2026 - Session Q&A report generation and delivery actions */}
            <div className="flex flex-wrap items-center gap-2 mx-2">



              {/* Download PowerPoint */}
              {canManageSessionQNAReportArtifacts && (
                <button
                  type="button"
                  disabled={
                    isProcessingSessionQNAReport
                  }
                  onClick={
                    handleDownloadSessionQNAPowerPoint
                  }
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-[10px] font-semibold transition ${
                    isDownloadingSessionQNAPowerPoint
                      ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                      : "border-lime-600 bg-lime-600 text-white hover:bg-lime-700"
                  }`}
                >
                  {
                    isDownloadingSessionQNAPowerPoint
                      ? "Generating PowerPoint..."
                      : "Download PowerPoint"
                  }
                </button>
              )}


              {/* Download PDF */}
              {canManageSessionQNAReportArtifacts && (
                <button
                  type="button"
                  disabled={
                    isProcessingSessionQNAReport
                  }
                  onClick={
                    handleDownloadSessionQNAPdf
                  }
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-[10px] font-semibold transition ${
                    isDownloadingSessionQNAPdf
                      ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                      : "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  {
                    isDownloadingSessionQNAPdf
                      ? "Generating PDF..."
                      : "Download PDF"
                  }
                </button>
              )}


              {/* Email Inline Report */}
              <button
                type="button"
                disabled={
                  isProcessingSessionQNAReport
                }
                onClick={
                  handleEmailSessionQNAInline
                }
                className={`shrink-0 rounded-full border px-3 py-1.5 text-[10px] font-semibold transition ${
                  isEmailingSessionQNAInline
                    ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                    : "border-orange-600 bg-orange-600 text-white hover:bg-orange-700"
                }`}
              >
                {
                  isEmailingSessionQNAInline
                    ? "Emailing Report..."
                    : "Email Report"
                }
              </button>


              {/* Email PowerPoint */}
              {canManageSessionQNAReportArtifacts && (
                <button
                  type="button"
                  disabled={
                    isProcessingSessionQNAReport
                  }
                  onClick={
                    handleEmailSessionQNAPowerPoint
                  }
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-[10px] font-semibold transition ${
                    isEmailingSessionQNAPowerPoint
                      ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                      : "border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700"
                  }`}
                >
                  {
                    isEmailingSessionQNAPowerPoint
                      ? "Emailing PowerPoint..."
                      : "Email PowerPoint"
                  }
                </button>
              )}


              {/* Email PDF */}
              {canEmailSessionQNAPdf && (
                <button
                  type="button"
                  disabled={
                    isProcessingSessionQNAReport
                  }
                  onClick={
                    handleEmailSessionQNAPdf
                  }
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-[10px] font-semibold transition ${
                    isEmailingSessionQNAPdf
                      ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                      : "border-purple-600 bg-purple-600 text-white hover:bg-purple-700"
                  }`}
                >
                  {
                    isEmailingSessionQNAPdf
                      ? "Emailing PDF..."
                      : "Email PDF"
                  }
                </button>
              )}

            </div>
            
            {/* <ChatBubbleLeftRightIcon className="h-5 w-5 text-orange-600" />
            <p className="text-[10px] text-gray-500 mx-1">
                {qnaContributionInsights?.totalResponses || 0} response
                {qnaContributionInsights?.totalResponses === 1 ? "" : "s"} ·{" "}
                {qnaContributionInsights?.totalResponders || 0} responder
                {qnaContributionInsights?.totalResponders === 1 ? "" : "s"} ·{" "}
                {qnaContributionInsights?.totalEligibleUsers || 0} eligible user
                {qnaContributionInsights?.totalEligibleUsers === 1 ? "" : "s"}
            </p> */}
          </div>
        </div>
      </div>

      {/* 2 - Board Content */}
      {showBoard && (
        <div className="m-1 p-1 bg-white rounded">

          {/* 15 Aug 2026 - Main Session Q&A display tabs */}
          <div className="mb-3 flex items-center overflow-x-auto border-b border-gray-300">

            <button
              type="button"
              onClick={() =>
                setActiveSessionQNATab(
                  "trends"
                )
              }
              className={`shrink-0 border-b-2 px-4 py-2 text-[10px] font-semibold transition ${
                activeSessionQNATab === "trends"
                  ? "border-orange-600 text-orange-600"
                  : "border-transparent text-gray-500 hover:text-orange-600"
              }`}
            >
              Q&A Response Trends
            </button>


            <button
              type="button"
              onClick={() =>
                setActiveSessionQNATab(
                  "responses"
                )
              }
              className={`shrink-0 border-b-2 px-4 py-2 text-[10px] font-semibold transition ${
                activeSessionQNATab === "responses"
                  ? "border-orange-600 text-orange-600"
                  : "border-transparent text-gray-500 hover:text-orange-600"
              }`}
            >
              Questions & Responses
            </button>

          </div>

          {/* 31 May 2026 - Session QNA response contribution insights */}
          {activeSessionQNATab === "trends" && (
            <>
              {renderQNAContributionInsights()}
            </>
          )}

          {activeSessionQNATab === "responses" && (
            <>
              {/* 2a - Empty Question State */}
              {boardQuestions?.length === 0 && (
                <div className="m-1 p-3 bg-gray-50 rounded">
                  <p className="text-xs text-gray-500">
                    No active Session Q&A questions are attached to this session.
                  </p>
                </div>
              )}

              {/* 2b - Questions */}
              {boardQuestions?.map((question, index) => {
                const questionKey = getQuestionKey(question)
                const questionResponses = getResponsesForQuestion(question)
                const ownResponse = getOwnResponseForQuestion(question)
                // 26 Jun 2026
                const hasCoachResponse = questionResponses?.some(
                  response => response?.responseRole === "Coach"
                )

                return (
                  <div
                    key={questionKey}
                    className="m-1 p-1 bg-gray-100 rounded"
                  >
                    {/* Question Header */}
                    <div className="flex justify-between items-start">
                      <div className="flex justify-start items-start">
                        <div
                          onClick={() => handleToggleQuestionDetails(questionKey)}
                          className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                        >
                          {showQuestionDetails[questionKey] ? (
                            <MinusIcon className="h-5 w-5 text-orange-600" />
                          ) : (
                            <PlusIcon className="h-5 w-5 text-orange-600" />
                          )}
                        </div>

                        <div className="m-1 p-1">
                          <div className="flex justify-start items-center flex-wrap">
                            <p className="text-xs font-bold text-gray-700">
                              Q{index + 1}. {question.questionText}
                            </p>

                            {question?.questionType && (
                              <p className="text-[9px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1">
                                {question.questionType}
                              </p>
                            )}

                            {ownResponse && (
                            <p className="text-[9px] bg-lime-100 text-lime-700 rounded-full mx-1 px-2 py-1">
                                My Response Added
                            </p>
                            )}

                            {hasCoachResponse && (
                              <p className="text-[9px] bg-yellow-100 text-gray-800 rounded-full mx-1 px-2 py-1">
                                Coach Response
                              </p>
                            )}
                          </div>

                          <p className="text-[9px] text-gray-500">
                            {question.sessionQNASetName}
                          </p>

                          {question?.questionPrompt && (
                            <p className="text-[10px] text-orange-700 italic mt-1">
                              {question.questionPrompt}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-end items-center m-1 p-1">
                        <ChatBubbleLeftRightIcon className="h-4 w-4 text-orange-600" />
                        <p className="text-[10px] text-gray-500 mx-1">
                          {questionResponses?.length || 0}
                        </p>
                      </div>
                    </div>

                    {/* Question Details */}
                    {showQuestionDetails[questionKey] && (
                      <div className="m-1 p-1 bg-white rounded">

                        {/* Description */}
                        {question?.questionDesc && (
                          <div className="m-1 p-2 bg-gray-50 rounded">
                            <p className="text-[10px] text-gray-600">
                              {question.questionDesc}
                            </p>
                          </div>
                        )}

                        {!ownResponse && !showResponseEditor[questionKey] && (
                            <div className="flex justify-start items-center m-1 p-1">
                                <p className="font-bold text-xs m-1 p-1">Add My Response</p>
                                <button
                                type="button"
                                onClick={() => handleOpenResponseEditor(question)}
                                >
                                <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-lime-600 hover:bg-green-600" />
                                </button>
                            </div>
                        )}

                        {/* My Response Editor - Opens only when user clicks edit or add response */}
                        {showResponseEditor[questionKey] && (
                        <div className="m-1 p-1 bg-gray-200 rounded">
                            <div className="flex justify-start items-center m-1 p-1">
                            <PencilSquareIcon className="h-4 w-4 text-orange-600 mx-1" />
                            <p className="text-xs font-bold text-gray-700">
                                {ownResponse ? "Edit My Response" : "Add My Response"}
                            </p>
                            </div>

                            <div className="bg-white w-full rounded p-2 m-1">
                            <EtContentEditorLight3
                                value={editorValues[questionKey] || ""}
                                handleChange={content =>
                                handleEditorChange(questionKey, content)
                                }
                                placeholderText={"Write your response here"}
                                toolbarId={`sessionQNAResponse-${questionKey}`}
                            />
                            </div>

                            <div className="bg-white m-1 px-1 py-3 rounded">
                            <EtBtnFormBasic
                                name={ownResponse ? "Update" : "Save"}
                                type={"create"}
                                handleClick={() => handleSaveResponse(question)}
                            />

                            <EtBtnFormBasic
                                name={"Cancel"}
                                type={"cancel"}
                                handleClick={() => handleCloseResponseEditor(question)}
                            />

                            {(isCreatingResponse || isUpdatingResponse) && (
                                <span className="text-[10px] text-orange-700 mx-2">
                                Saving...
                                </span>
                            )}
                            </div>
                        </div>
                        )}

                        {/* 26 Jun 2026 */}

                        {/* Cohort and Coach Responses */}
                        <div className="m-1 p-1 bg-gray-200 rounded">
                          {/*
                            26 Jun 2026 - Separate coach comments from cohort-user responses.
                            Both arrays are already cohort-filtered through qnaResponses.
                          */}
                          {(() => {
                            const coachResponses = questionResponses
                              ?.filter(response => response?.responseRole === "Coach")
                              ?.slice()
                              ?.sort(
                                (a, b) =>
                                  new Date(b?.updatedAt || b?.createdAt) -
                                  new Date(a?.updatedAt || a?.createdAt)
                              ) || []

                            const cohortUserResponses = questionResponses
                              ?.filter(response => response?.responseRole !== "Coach")
                              ?.slice()
                              ?.sort((a, b) => {
                                if (isOwnResponse(a)) return -1
                                if (isOwnResponse(b)) return 1

                                return (
                                  new Date(b?.updatedAt || b?.createdAt) -
                                  new Date(a?.updatedAt || a?.createdAt)
                                )
                              }) || []

                            return (
                              <>
                                {/* Coach Comments */}
                                <div className="m-1 p-1 bg-orange-50 border border-orange-200 rounded">
                                  <div className="flex justify-between items-center m-1 p-1">
                                    <div className="flex justify-start items-center">
                                      <AcademicCapIcon className="h-4 w-4 text-orange-600 mx-1" />

                                      <p className="text-xs font-bold text-orange-700">
                                        Coach Comments
                                      </p>
                                    </div>

                                    <div className="rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-white m-1">
                                      <p className="text-orange-600 text-[10px]">
                                        {coachResponses.length}
                                      </p>
                                    </div>
                                  </div>

                                  {coachResponses.length === 0 && (
                                    <div className="m-1 p-2 bg-white rounded">
                                      <p className="text-[10px] text-gray-500">
                                        No coach comments yet.
                                      </p>
                                    </div>
                                  )}

                                  {coachResponses.length > 0 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                                      {coachResponses.map(response =>
                                        renderResponseCard(response, question)
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Cohort User Responses */}
                                <div className="m-1 p-1 bg-white rounded">
                                  <div className="flex justify-between items-center m-1 p-1">
                                    <div className="flex justify-start items-center">
                                      <ChatBubbleLeftRightIcon className="h-4 w-4 text-orange-600 mx-1" />

                                      <p className="text-xs font-bold text-gray-700">
                                        Cohort Responses
                                      </p>
                                    </div>

                                    <div className="rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1">
                                      <p className="text-orange-600 text-[10px]">
                                        {cohortUserResponses.length}
                                      </p>
                                    </div>
                                  </div>

                                  {cohortUserResponses.length === 0 && (
                                    <div className="m-1 p-2 bg-gray-50 rounded">
                                      <p className="text-[10px] text-gray-500">
                                        No cohort responses yet.
                                      </p>
                                    </div>
                                  )}

                                  {cohortUserResponses.length > 0 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                                      {cohortUserResponses.map(response =>
                                        renderResponseCard(response, question)
                                      )}
                                    </div>
                                  )}
                                </div>
                              </>
                            )
                          })()}
                        </div>



                        {/* All Responses */}
                        {/* <div className="m-1 p-1 bg-gray-200 rounded">
                        <div className="flex justify-between items-center m-1 p-1">
                            <div className="flex justify-start items-center">
                            <ChatBubbleLeftRightIcon className="h-4 w-4 text-orange-600 mx-1" />
                            <p className="text-xs font-bold text-gray-700">
                                Cohort Responses
                            </p>
                            </div>

                            <div className="rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1">
                            <p className="text-orange-600 text-[10px]">
                                {questionResponses?.length || 0}
                            </p>
                            </div>
                        </div>

                        {questionResponses?.length === 0 && (
                            <div className="m-1 p-2 bg-white rounded">
                            <p className="text-[10px] text-gray-500">
                                No responses yet.
                            </p>
                            </div>
                        )}

                        {questionResponses?.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                            {questionResponses
                                .slice()
                                .sort((a, b) => {
                                if (isOwnResponse(a)) return -1
                                if (isOwnResponse(b)) return 1
                                if (a?.responseRole === "Coach") return -1
                                if (b?.responseRole === "Coach") return 1
                                return new Date(b?.updatedAt || b?.createdAt) - new Date(a?.updatedAt || a?.createdAt)
                                })
                                .map(response => renderResponseCard(response, question))}
                            </div>
                        )}
                        </div> */}
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}


        </div>
      )}
    </div>
  )
}

export default EtUserSessionQNABoard